import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchChapters,
  addChapter,
  deleteChapter,
  updateChapter,
  fetchClassesByBoardMedium,
  fetchLinkedSubjects,
  setSelectedMedium,
  setSelectedClass,
  setSelectedSubject,
  setChapterName as setChapterNameAction,
  resetAddChapterForm,
  resetChaptersList,
} from "../redux/chapterSlice";
import { fetchMediums } from "../redux/mediumSlice";
import Popup from "../../../Popup";
import Table from "../../Table";
import { TableSkeleton } from "../Skeleton";
import showToast from "../../../utils/toast";

const Chapter = () => {
  const [chapterName, setChapterName] = useState("");
  const [newChapterName, setNewChapterName] = useState("");
  const [editId, setEditId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const dispatch = useDispatch();
  const { boardId } = useParams();

  // Get data from Redux store
  const {
    items = [],
    loading,
    error,
    pagination = {},
  } = useSelector((state) => state.boardChapters || {});

  // Get mediums from mediumSlice
  const mediums = useSelector((state) => state.mediums.list);
  const mediumsLoading = useSelector((state) => state.mediums.loading);

  // Get add chapter form state from chapterSlice
  const {
    classes,
    subjects,
    selectedMedium,
    selectedClass,
    selectedSubject,
    classesLoading,
    subjectsLoading,
  } = useSelector((state) => state.boardChapters || {});

  // useEffect(() => {
  //   if (boardId) {
  //     console.log("Fetching chapters for boardId:", boardId);
  //     dispatch(fetchChapters(boardId));
  //   }
  // }, [dispatch, boardId]);

  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    setPage(1);
  }, [selectedMedium, selectedClass, selectedSubject]);

  // Reset state when board changes or when leaving the page
  useEffect(() => {
    dispatch(resetAddChapterForm());
    setPage(1);

    return () => {
      dispatch(resetAddChapterForm());
    };
  }, [boardId, dispatch]);

  useEffect(() => {
    // if (boardId) {
    if (boardId && selectedMedium && selectedClass && selectedSubject) {
      dispatch(
        fetchChapters({
          board_id: boardId,
          medium_id: selectedMedium || undefined,
          class_id: selectedClass || undefined,
          subject_id: selectedSubject || undefined,
          page,
          limit,
        }),
      );
    } else {
      dispatch(resetChaptersList());
    }
  }, [dispatch, boardId, selectedMedium, selectedClass, selectedSubject, page]);
  // Fetch mediums on component mount
  useEffect(() => {
    if (boardId) {
      dispatch(fetchMediums(boardId));
    }
  }, [boardId, dispatch]);

  // Fetch classes when medium is selected
  useEffect(() => {
    if (selectedMedium && boardId) {
      dispatch(
        fetchClassesByBoardMedium({
          board_id: boardId,
          medium_id: selectedMedium,
        }),
      );
    }
  }, [selectedMedium, boardId, dispatch]);

  // Fetch subjects when class is selected
  useEffect(() => {
    if (selectedClass) {
      dispatch(fetchLinkedSubjects(selectedClass));
    }
  }, [selectedClass, dispatch]);

  const handleSave = () => {
    if (!editId && !newChapterName.trim()) {
      alert("Please enter a chapter name");
      return;
    }

    if (editId) {
      if (!chapterName.trim()) {
        alert("Please enter a chapter name");
        return;
      }
      // UPDATE logic
      console.log("=== UPDATE CHAPTER ===");
      console.log("Chapter ID:", editId);
      console.log("Chapter Name:", chapterName);

      dispatch(updateChapter({ id: editId, name: chapterName })).then(() => {
        setEditId(null);
        setChapterName("");
        setShowPopup(false);
        dispatch(
          fetchChapters({
            board_id: boardId,
            medium_id: selectedMedium || undefined,
            class_id: selectedClass || undefined,
            subject_id: selectedSubject || undefined,
            page,
            limit,
          }),
        );
      });
    } else {
      // CREATE logic - now includes subject_id
      if (!selectedSubject) {
        alert("Please select a subject");
        return;
      }

      // Find selected item details for logging
      const selectedMediumObj = mediums.find((m) => m.id == selectedMedium);
      const selectedClassObj = classes.find((c) => c.class_id == selectedClass);
      const selectedSubjectObj = subjects.find((s) => s.id == selectedSubject);

      console.log("selectedSubjectObj", selectedSubjectObj);

      console.log("=== ADD NEW CHAPTER ===");
      console.log("Board ID:", boardId);
      console.log("Selected Medium:", {
        id: selectedMedium,
        name: selectedMediumObj?.name || "N/A",
      });
      console.log("Selected Class:", {
        id: selectedClass,
        name: selectedClassObj?.class_name || "N/A",
      });
      console.log("Selected Subject:", {
        id: selectedSubject,
        name: selectedSubjectObj?.subject_name || "N/A",
      });
      console.log("Chapter Name:", chapterName);
      console.log("======================");

      const payload = {
        board_id: parseInt(boardId),
        medium_id: parseInt(selectedMedium),
        class_id: parseInt(selectedClass),
        subject_id: parseInt(selectedSubject),
        // chapter_name: chapterName,
        chapter_name: newChapterName,
      };

      dispatch(addChapter(payload))
        .unwrap()
        .then((response) => {
          console.log("response", JSON.stringify(response));
          alert("Chapter created successfully");
          setNewChapterName("");

          setShowPopup(false);
          // dispatch(resetAddChapterForm());
          // dispatch(fetchChapters(boardId));
          dispatch(
            fetchChapters({
              board_id: boardId,
              medium_id: selectedMedium || undefined,
              class_id: selectedClass || undefined,
              subject_id: selectedSubject || undefined,
              page,
              limit,
            }),
          );
        })
        .catch((error) => {
          console.error("Error creating chapter:", error);
          alert(`Failed to create chapter: ${error.message || error}`);
        });
    }
  };

  const startEdit = (chapter) => {
    setEditId(chapter.chapter_id);
    setChapterName(chapter.chapter_name);
    setShowPopup(true);
  };

  const cancelEdit = () => {
    setEditId(null);
    setChapterName("");
    dispatch(resetAddChapterForm());
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this chapter?")) return;
    try {
      await dispatch(deleteChapter(id)).unwrap();
      showToast.success("Chapter deleted successfully");
      dispatch(
        fetchChapters({
          board_id: boardId,
          medium_id: selectedMedium || undefined,
          class_id: selectedClass || undefined,
          subject_id: selectedSubject || undefined,
          page,
          limit,
        }),
      );
    } catch (error) {
      showToast.error(error);
    }
  };

  const isFormValid = editId
    ? chapterName.trim()
    : chapterName.trim() && selectedMedium && selectedClass && selectedSubject;
  const isFormValidNew =
    newChapterName.trim() ||
    (!newChapterName.trim() &&
      !selectedMedium &&
      !selectedClass &&
      !selectedSubject);

  // Define table columns
  const columns = [
    {
      header: "Medium",
      accessor: "medium_name",
      align: "left",
    },
    {
      header: "Class",
      accessor: "class_name",
      align: "left",
    },
    {
      header: "Subject",
      accessor: "subject_name",
      align: "left",
    },
    {
      header: "Chapter Name",
      accessor: "chapter_name",
      align: "left",
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <i
            className="bi bi-pencil-square"
            style={{
              fontSize: "18px",
              color: "#3b82f6",
              cursor: "pointer",
            }}
            title="Edit"
            onClick={() => startEdit(row)}
          />
          <i
            className="bi bi-trash"
            style={{
              fontSize: "18px",
              color: "#dc2626",
              cursor: "pointer",
            }}
            title="Delete"
            onClick={() => handleDelete(row.chapter_id)}
          />
        </div>
      ),
    },
  ];

  // Show message if no boardId
  if (!boardId) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "8px",
              }}
            >
              Board not selected
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
              Please select a board first to view chapters.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // marginBottom: "24px",
            marginBottom: "8px",
            // border: "1px solid black  ",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Chapters
          </h2>
          <p>Total Records Count : {pagination?.totalRecords || 0}</p>
          {false && (
            <button
              onClick={() => {
                // cancelEdit();
                if (!selectedMedium || !selectedClass || !selectedSubject) {
                  alert("Please select Medium, Class and Subject first");
                  return;
                }
                setShowPopup(true);
              }}
              disabled={!boardId}
              style={{
                backgroundColor: boardId ? "#3b82f6" : "#9ca3af",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: boardId ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (boardId) e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (boardId) e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              <span style={{ fontSize: "18px" }}>+</span> Add Chapter
            </button>
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            // gap: "20px",
            // marginBottom: "20px",
            alignItems: "end",
            gap: "12px",
            marginBottom: "8px",
            marginTop: "2px",
            // border: "1px solid red",
          }}
        >
          {/* MEDIUM SELECTION */}
          <div>
            {/* <div style={{ marginBottom: "20px" }}> */}
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Select Medium <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={selectedMedium || ""}
              onChange={(e) => dispatch(setSelectedMedium(e.target.value))}
              disabled={mediumsLoading}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
                backgroundColor: mediumsLoading ? "#f9fafb" : "white",
                cursor: mediumsLoading ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            >
              <option value="">
                {mediumsLoading ? "Loading mediums..." : "-- Select Medium --"}
              </option>
              {mediums.map((medium) => (
                <option key={medium.id} value={medium.id}>
                  {medium.name}
                </option>
              ))}
            </select>
          </div>
          {/* CLASS SELECTION */}
          {/* {selectedMedium && ( */}
          <div>
            {/* <div style={{ marginBottom: "20px" }}> */}
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Select Class <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={selectedClass || ""}
              onChange={(e) => dispatch(setSelectedClass(e.target.value))}
              disabled={classesLoading}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
                backgroundColor: classesLoading ? "#f9fafb" : "white",
                cursor: classesLoading ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            >
              <option value="">
                {classesLoading ? "Loading classes..." : "-- Select Class --"}
              </option>
              {classes.map((classItem) => (
                <option key={classItem.class_id} value={classItem.class_id}>
                  {classItem.class_name}
                </option>
              ))}
            </select>
          </div>
          {/* )} */}
          {/* SUBJECT SELECTION */}
          {/* {selectedClass && ( */}
          <div>
            {/* <div style={{ marginBottom: "20px" }}> */}
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Select Subject <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={selectedSubject || ""}
              onChange={(e) => dispatch(setSelectedSubject(e.target.value))}
              disabled={subjectsLoading}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
                backgroundColor: subjectsLoading ? "#f9fafb" : "white",
                cursor: subjectsLoading ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            >
              <option value="">
                {subjectsLoading
                  ? "Loading subjects..."
                  : "-- Select Subject --"}
              </option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.subject_name}
                </option>
              ))}
            </select>
          </div>
          {/* CHAPTER NAME INPUT */}
          <div
            style={{
              marginBottom: "4px",
              display: "flex",
              alignItems: "flex-end",
              gap: "12px",
              width: "200%",
              // border: "1px solid #e5e7eb",
              padding: "10px",
            }}
          >
            <div style={{ flexGrow: 4 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Chapter Name <span style={{ color: "#dc2626" }}>*</span>
              </label>

              <input
                type="text"
                // value={chapterName}
                // onChange={(e) => setChapterName(e.target.value)}
                value={newChapterName}
                onChange={(e) => setNewChapterName(e.target.value)}
                placeholder="Enter chapter name"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  color: "#111827",
                  outline: "none",
                  background: !selectedSubject ? "rgb(242,242,242)" : "#ffffff",
                }}
                disabled={!selectedSubject}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              />
            </div>

            <button
              onClick={() => {
                if (!selectedClass || !selectedMedium || !selectedSubject) {
                  alert(
                    "please select the class,subject and medium inorder to create the chapter",
                  );
                } else {
                  handleSave();
                }
              }}
              // disabled={!isFormValidNew || loading}
              // disabled={!isFormValidNew}
              style={{
                height: "40px",
                minWidth: "160px",
                padding: "0 18px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                backgroundColor:
                  // isFormValidNew && !loading ? "#3b82f6" : "#9ca3af",
                  !loading ? "#3b82f6" : "#9ca3af",
                // cursor: isFormValidNew && !loading ? "pointer" : "not-allowed",
                cursor: !loading ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
              onMouseOver={(e) => {
                if (isFormValid && !loading)
                  e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (isFormValid && !loading)
                  e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              {loading && (
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
              )}

              {loading ? "Saving..." : "Save Chapter"}
            </button>
          </div>
        </div>

        {/* TABLE */}
        {loading ? (
          <TableSkeleton rows={5} columns={2} />
        ) : error ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#fef2f2",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #fecaca",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>⚠️</div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#dc2626",
                marginBottom: "8px",
              }}
            >
              Failed to load chapters
            </h3>
            <p
              style={{
                color: "#991b1b",
                fontSize: "14px",
                marginBottom: "16px",
              }}
            >
              {typeof error === "string"
                ? error
                : "An error occurred while loading chapters"}
            </p>
            <button
              onClick={() => dispatch(fetchChapters(boardId))}
              style={{
                padding: "8px 16px",
                backgroundColor: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#b91c1c")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#dc2626")}
            >
              Try Again
            </button>
          </div>
        ) : items.length > 0 ? (
          <>
            <Table
              columns={columns}
              data={items}
              serialNumberOffset={(page - 1) * limit}
            />
            {/* PAGINATION */}
            {pagination?.totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "30px",
                  flexWrap: "wrap",
                }}
              >
                {/* PREV */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    border: "none",
                    background:
                      page === 1
                        ? "#e5e7eb"
                        : "linear-gradient(135deg, #6366f1, #3b82f6)",
                    color: page === 1 ? "#9ca3af" : "white",
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    boxShadow:
                      page === 1
                        ? "none"
                        : "0 4px 10px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  ‹
                </button>

                {/* PAGE NUMBERS */}
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .slice(
                    Math.max(0, page - 3),
                    Math.min(pagination.totalPages, page + 2),
                  )
                  .map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        border: "none",
                        background:
                          pageNumber === page
                            ? "linear-gradient(135deg, #ec4899, #f43f5e)"
                            : "#f3f4f6",
                        color: pageNumber === page ? "white" : "#374151",
                        fontWeight: "600",
                        cursor: "pointer",
                        boxShadow:
                          pageNumber === page
                            ? "0 4px 12px rgba(244, 63, 94, 0.4)"
                            : "0 2px 6px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {pageNumber}
                    </button>
                  ))}

                {/* NEXT */}
                <button
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    border: "none",
                    background:
                      page === pagination.totalPages
                        ? "#e5e7eb"
                        : "linear-gradient(135deg, #6366f1, #3b82f6)",
                    color: page === pagination.totalPages ? "#9ca3af" : "white",
                    cursor:
                      page === pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                    boxShadow:
                      page === pagination.totalPages
                        ? "none"
                        : "0 4px 10px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : !selectedMedium || !selectedClass || !selectedSubject ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              No chapters found. Click "Add Chapter" to create one.
            </p>
          </div>
        ) : selectedMedium &&
          selectedClass &&
          selectedSubject &&
          items.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              No chapters found. Click "Add Chapter" to create one.
            </p>
          </div>
        ) : (
          <></>
        )}

        {/* POPUP */}
        {showPopup && (
          <Popup
            onClose={() => {
              setShowPopup(false);
              // cancelEdit();
            }}
            title={editId ? "Edit Chapter" : "Add New Chapter"}
            maxWidth="500px"
          >
            {editId ? (
              // EDIT MODE - Simple chapter name input
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Chapter Name <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  value={chapterName}
                  onChange={(e) => setChapterName(e.target.value)}
                  placeholder="Enter chapter name"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    color: "#111827",
                    outline: "none",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                />
              </div>
            ) : (
              // ADD MODE - Cascading selection
              <>
                {/* CHAPTER NAME INPUT */}
                {selectedSubject && (
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Chapter Name <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={chapterName}
                      onChange={(e) => setChapterName(e.target.value)}
                      placeholder="Enter chapter name"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#111827",
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                      onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                    />
                  </div>
                )}
              </>
            )}

            {/* ACTIONS */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                paddingTop: "16px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                onClick={() => {
                  setShowPopup(false);
                  // cancelEdit();
                }}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  backgroundColor: "white",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#f9fafb")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isFormValid || loading}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "white",
                  backgroundColor:
                    isFormValid && !loading ? "#3b82f6" : "#9ca3af",
                  cursor: isFormValid && !loading ? "pointer" : "not-allowed",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) => {
                  if (isFormValid && !loading)
                    e.target.style.backgroundColor = "#2563eb";
                }}
                onMouseOut={(e) => {
                  if (isFormValid && !loading)
                    e.target.style.backgroundColor = "#3b82f6";
                }}
              >
                {loading && (
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                )}
                {loading
                  ? "Saving..."
                  : editId
                    ? "Update Chapter"
                    : "Save Chapter"}
                f{" "}
              </button>
            </div>
          </Popup>
        )}
      </div>
    </>
  );
};

export default Chapter;
